<template>
  <div class="space-y-8">
    <section class="overflow-hidden rounded-[32px] border border-orange-100 bg-[radial-gradient(circle_at_top_left,_rgba(255,193,136,0.42),_transparent_34%),linear-gradient(135deg,#fff8f1_0%,#ffffff_52%,#fff2e5_100%)] p-6 shadow-sm lg:p-8">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.28em] text-brand">Vendor dashboard</p>
        <h2 class="mt-3 max-w-3xl text-3xl font-semibold leading-tight text-ink">Manage each business with its own venues and service vendors.</h2>
        <p class="mt-3 max-w-3xl text-sm text-muted">Each business card contains its child modules. Add the business first, then manage venues and service vendors inside it.</p>

        <div class="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
            @click="openServiceModal()"
          >
            {{ hasBusiness ? 'Add another business' : 'Add business' }}
          </button>
        </div>
      </div>
    </section>

    <section class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-lg font-semibold text-ink">{{ hasBusiness ? 'Businesses' : 'Add your business' }}</h3>
          <p class="text-sm text-muted">Each business contains its own venue and service vendor modules.</p>
        </div>
        <div class="flex flex-wrap gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" :disabled="serviceListLoading" @click="fetchBusinessVendors()">
            {{ serviceListLoading ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button type="button" class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="openServiceModal()">
            {{ hasBusiness ? 'Add another business' : 'Add business' }}
          </button>
        </div>
      </div>

      <div v-if="serviceListError" class="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
        {{ serviceListError }}
      </div>

      <div v-if="serviceListLoading" class="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-muted">
        Loading business vendors...
      </div>

      <div v-else-if="serviceVendors.length" class="space-y-5">
        <article v-for="business in serviceVendors" :key="business.id" class="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p class="text-3xl font-semibold text-ink">{{ business.businessName }}</p>
              <p class="mt-2 text-sm text-muted">{{ business.badge }}<span v-if="business.address"> • {{ business.address }}</span></p>
            </div>
            <div class="relative">
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-lg text-ink transition hover:border-brand hover:text-brand"
                @click="toggleBusinessMenu(business.id)"
              >
                <i class="ri-more-2-fill"></i>
              </button>
              <div
                v-if="openBusinessMenuId === business.id"
                class="absolute right-0 top-14 z-20 min-w-[190px] rounded-2xl border border-gray-100 bg-white p-2 shadow-xl"
              >
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-ink transition hover:bg-[#fff3e5] hover:text-brand" @click="handleVendorMenuAction('edit', business)">
                  Edit business
                </button>
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-ink transition hover:bg-[#fff3e5] hover:text-brand" @click="handleVendorMenuAction('edit-contact', business)">
                  Edit contact info
                </button>
                <button type="button" class="flex w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50" @click="handleVendorMenuAction('delete', business)">
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div class="mt-6 border-l-2 border-gray-200 pl-6">
            <div class="flex flex-wrap gap-3">
              <button type="button" class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark" @click="openVenueModal(null, business)">
                <i class="ri-building-line mr-2"></i>Add Venue
              </button>
              <button type="button" class="rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="openServiceVendorModal(null, business)">
                <i class="ri-store-2-line mr-2"></i>Add Service Vendor
              </button>
            </div>

            <div class="mt-5 grid gap-4 lg:grid-cols-2">
              <article
                v-for="venue in getBusinessVenues(business.id)"
                :key="`venue-${venue.id}`"
                class="rounded-3xl border border-gray-200 bg-[#fffdf9] p-5"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-2xl font-semibold text-ink">{{ venue.name }}</p>
                    <p class="mt-2 text-sm text-muted"><i class="ri-building-line mr-1"></i>{{ venue.type }}<span v-if="venue.city"> • {{ venue.city }}</span></p>
                  </div>
                  <span class="rounded-full px-3 py-1 text-xs font-semibold" :class="venue.status === 'Live' ? 'bg-[#FFF3E5] text-brand' : 'bg-gray-100 text-gray-600'">
                    {{ venue.status }}
                  </span>
                </div>
                <p class="mt-4 text-sm text-muted">{{ venue.description }}</p>
                <div class="mt-4 flex gap-3">
                  <button type="button" class="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand" @click="openVenueModal(venue, business)">
                    Edit
                  </button>
                  <button type="button" class="rounded-xl border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300" @click="deleteVenue(venue.id)">
                    Delete
                  </button>
                </div>
              </article>

              <article
                v-for="module in getBusinessServiceModules(business.id)"
                :key="`service-${module.id}`"
                class="rounded-3xl border border-gray-200 bg-[#fffdf9] p-5"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-2xl font-semibold text-ink">{{ module.name }}</p>
                    <p class="mt-2 text-sm text-muted"><i class="ri-store-2-line mr-1"></i>{{ module.category }}</p>
                  </div>
                </div>
                <p class="mt-4 text-sm text-muted">{{ module.description }}</p>
                <div class="mt-4 flex gap-3">
                  <button type="button" class="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand" @click="openServiceVendorModal(module, business)">
                    Edit
                  </button>
                  <button type="button" class="rounded-xl border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300" @click="deleteServiceModule(module.id)">
                    Delete
                  </button>
                </div>
              </article>

              <div
                v-if="!getBusinessVenues(business.id).length && !getBusinessServiceModules(business.id).length"
                class="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-muted lg:col-span-2"
              >
                No venue or service vendor modules added for this business yet.
              </div>
            </div>
          </div>
        </article>
      </div>

      <div v-else class="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-muted">
        Add your business first. After that, its venue and service vendor modules will become available.
      </div>
    </section>

    <div v-if="hasBusiness && venueModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4 py-8" @click.self="closeVenueModal">
      <div class="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-xl font-semibold text-ink">{{ editingVenueId ? 'Edit venue module' : 'Add venue module' }}</h3>
            <p class="text-sm text-muted">Add the venue details for {{ activeBusinessName || 'the selected business' }}.</p>
          </div>
          <button type="button" class="rounded-full border border-gray-200 px-3 py-2 text-sm text-muted" @click="closeVenueModal">Close</button>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <label class="text-sm font-semibold text-ink">
            Venue name
            <input v-model.trim="venueForm.name" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink">
            Venue type
            <input v-model.trim="venueForm.type" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink">
            City
            <input v-model.trim="venueForm.city" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink">
            Capacity
            <input v-model.trim="venueForm.capacity" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink">
            Price
            <input v-model.trim="venueForm.price" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink">
            Status
            <select v-model="venueForm.status" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
              <option>Draft</option>
              <option>Live</option>
            </select>
          </label>
          <label class="text-sm font-semibold text-ink md:col-span-2">
            Description
            <textarea v-model.trim="venueForm.description" rows="4" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none"></textarea>
          </label>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-ink" @click="closeVenueModal">Cancel</button>
          <button type="button" class="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white" @click="saveVenue">Save venue module</button>
        </div>
      </div>
    </div>

    <div v-if="serviceVendorModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4 py-8" @click.self="closeServiceVendorModal">
      <div class="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-xl font-semibold text-ink">{{ editingServiceModuleId ? 'Edit service vendor' : 'Add service vendor' }}</h3>
            <p class="text-sm text-muted">Add the service vendor details for {{ activeBusinessName || 'the selected business' }}.</p>
          </div>
          <button type="button" class="rounded-full border border-gray-200 px-3 py-2 text-sm text-muted" @click="closeServiceVendorModal">Close</button>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <label class="text-sm font-semibold text-ink">
            Service vendor name
            <input v-model.trim="serviceVendorForm.name" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink">
            Category
            <input v-model.trim="serviceVendorForm.category" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none">
          </label>
          <label class="text-sm font-semibold text-ink md:col-span-2">
            Description
            <textarea v-model.trim="serviceVendorForm.description" rows="4" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none"></textarea>
          </label>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-ink" @click="closeServiceVendorModal">Cancel</button>
          <button type="button" class="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white" @click="saveServiceModule">Save service vendor</button>
        </div>
      </div>
    </div>

    <div v-if="businessContactModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4 py-8" @click.self="closeBusinessContactModal">
      <div class="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-xl font-semibold text-ink">Edit contact info</h3>
            <p class="text-sm text-muted">Update the contact details for {{ activeBusinessName || 'the selected business' }}.</p>
          </div>
          <button type="button" class="rounded-full border border-gray-200 px-3 py-2 text-sm text-muted" @click="closeBusinessContactModal">Close</button>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <label class="text-sm font-semibold text-ink">
            Phone code
            <input v-model.trim="businessContactForm.phoneCode" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="+91">
          </label>
          <label class="text-sm font-semibold text-ink">
            Phone
            <input v-model.trim="businessContactForm.phone" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="9876543210">
          </label>
          <label class="text-sm font-semibold text-ink">
            Email
            <input v-model.trim="businessContactForm.email" type="email" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="name@example.com">
          </label>
          <label class="text-sm font-semibold text-ink">
            Website
            <input v-model.trim="businessContactForm.website" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="https://example.com">
          </label>
          <label class="text-sm font-semibold text-ink md:col-span-2">
            Address
            <input v-model.trim="businessContactForm.address" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="Business address">
          </label>
          <label class="text-sm font-semibold text-ink">
            Latitude
            <input v-model.trim="businessContactForm.latitude" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="17.3850">
          </label>
          <label class="text-sm font-semibold text-ink">
            Longitude
            <input v-model.trim="businessContactForm.longitude" type="text" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" placeholder="78.4867">
          </label>
        </div>

        <p v-if="businessContactModalError" class="mt-5 text-sm font-semibold text-red-600">{{ businessContactModalError }}</p>

        <div class="mt-6 flex justify-end gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-ink" @click="closeBusinessContactModal">Cancel</button>
          <button type="button" class="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60" :disabled="businessContactSubmitLoading" @click="saveBusinessContactInfo">
            {{ businessContactSubmitLoading ? 'Saving...' : 'Save contact info' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="serviceModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4 py-8" @click.self="closeServiceModal">
      <div class="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-2xl">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-xl font-semibold text-ink">{{ editingServiceId ? 'Edit business' : 'Add business' }}</h3>
            <p class="text-sm text-muted">Create the parent business record first. Venue and service vendor entries depend on this setup.</p>
          </div>
          <button type="button" class="rounded-full border border-gray-200 px-3 py-2 text-sm text-muted" @click="closeServiceModal">Close</button>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <label class="text-sm font-semibold text-ink">
            Business name
            <input v-model.trim="serviceForm.businessName" type="text" class="mt-2 w-full rounded-2xl border px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" :class="serviceErrors.businessName ? 'border-red-300' : 'border-gray-200'">
            <span v-if="serviceErrors.businessName" class="mt-2 block text-xs font-semibold text-red-600">{{ serviceErrors.businessName }}</span>
          </label>
          <label class="text-sm font-semibold text-ink">
            Owner name
            <input v-model.trim="serviceForm.ownerName" type="text" class="mt-2 w-full rounded-2xl border px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" :class="serviceErrors.ownerName ? 'border-red-300' : 'border-gray-200'">
            <span v-if="serviceErrors.ownerName" class="mt-2 block text-xs font-semibold text-red-600">{{ serviceErrors.ownerName }}</span>
          </label>
          <label class="text-sm font-semibold text-ink md:col-span-2">
            Description
            <textarea v-model.trim="serviceForm.description" rows="4" class="mt-2 w-full rounded-2xl border px-4 py-3 text-sm font-normal text-ink focus:border-brand focus:outline-none" :class="serviceErrors.description ? 'border-red-300' : 'border-gray-200'"></textarea>
            <span v-if="serviceErrors.description" class="mt-2 block text-xs font-semibold text-red-600">{{ serviceErrors.description }}</span>
          </label>

          <div class="text-sm font-semibold text-ink md:col-span-2">
            Image
            <label class="mt-2 flex cursor-pointer items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-[#fffaf5] px-5 py-8 text-center transition hover:border-brand">
              <input type="file" accept="image/*" class="hidden" @change="handleServiceImageChange">
              <span class="text-sm font-medium text-muted">Choose image</span>
            </label>
            <span v-if="serviceErrors.image" class="mt-2 block text-xs font-semibold text-red-600">{{ serviceErrors.image }}</span>

            <div v-if="serviceImagePreview" class="mt-4 overflow-hidden rounded-3xl border border-gray-200 bg-white">
              <img :src="serviceImagePreview" alt="Service vendor preview" class="h-56 w-full object-cover">
              <div class="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
                <p class="truncate text-sm font-medium text-ink">{{ serviceForm.imageName }}</p>
                <button type="button" class="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300" @click="removeServiceImage">
                  Remove image
                </button>
              </div>
            </div>
          </div>
        </div>

        <p v-if="serviceModalError" class="mt-5 text-sm font-semibold text-red-600">{{ serviceModalError }}</p>

        <div class="mt-6 flex justify-end gap-3">
          <button type="button" class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-ink" @click="closeServiceModal">Cancel</button>
          <button type="button" class="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60" :disabled="serviceSubmitLoading" @click="saveServiceVendor">
            {{ serviceSubmitLoading ? 'Saving...' : editingServiceId ? 'Save changes' : 'Save business' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import Swal from 'sweetalert2'
import api from '@/services/api'
import { getAuthUser, getAuthUserId } from '@/services/auth'

const VENUES_STORAGE_KEY = 'vendor_dashboard_venues'
const SERVICE_MODULES_STORAGE_KEY = 'vendor_dashboard_service_modules'
const UPDATED_STORAGE_KEY = 'vendor_dashboard_last_updated'

const readStoredList = (key, fallback) => {
  if (typeof window === 'undefined') return fallback
  const raw = window.localStorage.getItem(key)
  if (!raw) return fallback
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

const venues = ref(readStoredList(VENUES_STORAGE_KEY, []))
const serviceModules = ref(readStoredList(SERVICE_MODULES_STORAGE_KEY, []))
const serviceVendors = ref([])
const venueModalOpen = ref(false)
const serviceVendorModalOpen = ref(false)
const businessContactModalOpen = ref(false)
const serviceModalOpen = ref(false)
const editingVenueId = ref(null)
const editingServiceModuleId = ref(null)
const editingServiceId = ref(null)
const editingBusinessContactId = ref(null)
const activeBusinessId = ref(null)
const vendorId = ref(getAuthUserId())
const lastUpdated = ref(typeof window !== 'undefined' ? window.localStorage.getItem(UPDATED_STORAGE_KEY) || '' : '')
const serviceListLoading = ref(false)
const serviceListError = ref('')
const serviceSubmitLoading = ref(false)
const businessContactSubmitLoading = ref(false)
const serviceModalError = ref('')
const businessContactModalError = ref('')
const serviceImagePreview = ref('')
const openBusinessMenuId = ref(null)

const venueForm = reactive({
  businessId: '',
  name: '',
  type: '',
  city: '',
  capacity: '',
  price: '',
  status: 'Draft',
  description: ''
})

const serviceVendorForm = reactive({
  id: null,
  businessId: '',
  name: '',
  category: '',
  description: ''
})

const businessContactForm = reactive({
  phoneCode: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  latitude: '',
  longitude: ''
})

const serviceForm = reactive({
  id: null,
  businessName: '',
  ownerName: '',
  description: '',
  imageFile: null,
  imageName: '',
  existingImage: '',
  badge: 'Business vendor'
})

const serviceErrors = reactive({
  businessName: '',
  ownerName: '',
  description: '',
  image: ''
})

const clearServiceErrors = () => {
  serviceErrors.businessName = ''
  serviceErrors.ownerName = ''
  serviceErrors.description = ''
  serviceErrors.image = ''
}

const hasBusiness = computed(() => serviceVendors.value.length > 0)
const activeBusinessName = computed(() => serviceVendors.value.find((item) => String(item.id) === String(activeBusinessId.value))?.businessName || '')

watch(
  venues,
  (value) => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(VENUES_STORAGE_KEY, JSON.stringify(value))
  },
  { deep: true }
)

watch(
  serviceModules,
  (value) => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SERVICE_MODULES_STORAGE_KEY, JSON.stringify(value))
  },
  { deep: true }
)

const updateLastUpdated = () => {
  lastUpdated.value = new Date().toISOString()
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(UPDATED_STORAGE_KEY, lastUpdated.value)
  }
}

const normalizeImage = (item) => item?.img || item?.image || item?.business_img || item?.photo || ''

const normalizeBusinessVendor = (item, index) => ({
  id: item?.id || item?.business_vendor_id || item?.vendor_id || item?.business_id || `vendor-${index}`,
  sId: item?.s_id || item?.seller_id || item?.vendor_owner_id || '',
  businessName: item?.business_name || item?.name || item?.title || 'Untitled business vendor',
  ownerName: item?.owner_name || item?.owner || item?.contact_name || 'Owner not available',
  description: item?.description || item?.about || item?.details || 'No description available.',
  image: normalizeImage(item),
  phoneCode: item?.phone_code ?? '',
  phone: item?.phone ?? '',
  email: item?.email ?? '',
  address: item?.address ?? item?.location ?? '',
  website: item?.website ?? '',
  latitude: item?.latitude ?? '',
  longitude: item?.longitude ?? '',
  badge: item?.status || item?.type || 'Business vendor'
})

const normalizeVenue = (item, index) => ({
  id: item?.id || item?.venue_id || `venue-${index}`,
  businessId: item?.business_id || item?.parent_business_id || item?.s_id || '',
  name: item?.venue_name || item?.name || 'Untitled venue',
  type: item?.venue_type || item?.type || 'Venue',
  city: item?.city || item?.location || 'Unknown city',
  capacity: item?.capacity || item?.guest_capacity || '—',
  price: item?.price || item?.starting_price || 'On request',
  status: item?.status || 'Draft',
  description: item?.description || item?.about || 'No description available.'
})

const getListCollections = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) {
    return {
      vendorItems: payload,
      venueItems: []
    }
  }
  return {
    vendorItems:
      payload?.vendors ||
      payload?.business_vendors ||
      payload?.businessVendor ||
      payload?.items ||
      payload?.list ||
      [],
    venueItems:
      payload?.venues ||
      payload?.venue_list ||
      payload?.business_venues ||
      []
  }
}

const fetchBusinessVendors = async () => {
  vendorId.value = getAuthUserId()
  serviceListError.value = ''

  if (!vendorId.value) {
    serviceListError.value = 'Vendor id (`s_id`) is not available in auth storage.'
    serviceVendors.value = []
    return
  }

  serviceListLoading.value = true
  try {
    const formData = new FormData()
    formData.append('s_id', vendorId.value)
    const { data } = await api.listBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to load business vendors')
    }

    const collections = getListCollections(data)
    const normalizedVendors = Array.isArray(collections.vendorItems)
      ? collections.vendorItems.map(normalizeBusinessVendor)
      : []
    const normalizedVenues = Array.isArray(collections.venueItems)
      ? collections.venueItems.map(normalizeVenue)
      : []

    serviceVendors.value = normalizedVendors

    if (normalizedVenues.length) {
      venues.value = normalizedVenues
    }
  } catch (error) {
    serviceListError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to load business vendors'
    serviceVendors.value = []
  } finally {
    serviceListLoading.value = false
  }
}

const resetVenueForm = () => {
  venueForm.businessId = ''
  venueForm.name = ''
  venueForm.type = ''
  venueForm.city = ''
  venueForm.capacity = ''
  venueForm.price = ''
  venueForm.status = 'Draft'
  venueForm.description = ''
  editingVenueId.value = null
}

const resetServiceVendorForm = () => {
  serviceVendorForm.id = null
  serviceVendorForm.businessId = ''
  serviceVendorForm.name = ''
  serviceVendorForm.category = ''
  serviceVendorForm.description = ''
  editingServiceModuleId.value = null
}

const resetBusinessContactForm = () => {
  businessContactForm.phoneCode = ''
  businessContactForm.phone = ''
  businessContactForm.email = ''
  businessContactForm.website = ''
  businessContactForm.address = ''
  businessContactForm.latitude = ''
  businessContactForm.longitude = ''
  businessContactModalError.value = ''
  editingBusinessContactId.value = null
}

const revokeImagePreview = () => {
  if (serviceImagePreview.value && serviceImagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(serviceImagePreview.value)
  }
}

const resetServiceForm = () => {
  serviceForm.id = null
  serviceForm.businessName = ''
  serviceForm.ownerName = ''
  serviceForm.description = ''
  serviceForm.imageFile = null
  serviceForm.imageName = ''
  serviceForm.existingImage = ''
  serviceForm.badge = 'Business vendor'
  clearServiceErrors()
  serviceModalError.value = ''
  editingServiceId.value = null
  revokeImagePreview()
  serviceImagePreview.value = ''
}

const getBusinessVenues = (businessId) => venues.value.filter((item) => String(item.businessId) === String(businessId))
const getBusinessServiceModules = (businessId) => serviceModules.value.filter((item) => String(item.businessId) === String(businessId))

const openVenueModal = (venue = null, business = null) => {
  if (!hasBusiness.value) return
  activeBusinessId.value = business?.id || venue?.businessId || null
  if (venue) {
    editingVenueId.value = venue.id
    venueForm.businessId = venue.businessId
    venueForm.name = venue.name
    venueForm.type = venue.type
    venueForm.city = venue.city
    venueForm.capacity = venue.capacity
    venueForm.price = venue.price
    venueForm.status = venue.status
    venueForm.description = venue.description
  } else {
    resetVenueForm()
    venueForm.businessId = business?.id || ''
  }
  venueModalOpen.value = true
}

const closeVenueModal = () => {
  venueModalOpen.value = false
  resetVenueForm()
  activeBusinessId.value = null
}

const saveVenue = async () => {
  const isEditing = Boolean(editingVenueId.value)
  const payload = {
    id: editingVenueId.value || Date.now(),
    businessId: venueForm.businessId || activeBusinessId.value || '',
    name: venueForm.name || 'Untitled venue',
    type: venueForm.type || 'Venue',
    city: venueForm.city || 'Unknown city',
    capacity: venueForm.capacity || '—',
    price: venueForm.price || 'On request',
    status: venueForm.status || 'Draft',
    description: venueForm.description || 'No description added yet.'
  }

  if (editingVenueId.value) {
    venues.value = venues.value.map((item) => (item.id === editingVenueId.value ? payload : item))
  } else {
    venues.value.unshift(payload)
  }

  updateLastUpdated()
  closeVenueModal()
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title: isEditing ? 'Venue updated' : 'Venue added',
    showConfirmButton: false,
    timer: 1600,
    timerProgressBar: true
  })
}

const deleteVenue = async (id) => {
  const result = await Swal.fire({
    title: 'Delete venue?',
    text: 'This venue card will be removed from the dashboard.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626'
  })

  if (!result.isConfirmed) return

  venues.value = venues.value.filter((item) => item.id !== id)
  updateLastUpdated()
}

const openServiceModal = (vendor = null) => {
  openBusinessMenuId.value = null
  resetServiceForm()
  if (vendor) {
    editingServiceId.value = vendor.id
    serviceForm.id = vendor.id
    serviceForm.businessName = vendor.businessName
    serviceForm.ownerName = vendor.ownerName
    serviceForm.description = vendor.description
    serviceForm.existingImage = vendor.image
    serviceForm.imageName = vendor.image ? 'Current image' : ''
    serviceForm.badge = vendor.badge || 'Business vendor'
    serviceImagePreview.value = vendor.image || ''
  }
  serviceModalOpen.value = true
}

const closeServiceModal = () => {
  serviceModalOpen.value = false
  resetServiceForm()
}

const toggleBusinessMenu = (businessId) => {
  openBusinessMenuId.value = openBusinessMenuId.value === businessId ? null : businessId
}

const openBusinessContactModal = (business) => {
  openBusinessMenuId.value = null
  editingBusinessContactId.value = business.id
  activeBusinessId.value = business.id
  businessContactForm.phoneCode = business.phoneCode ?? ''
  businessContactForm.phone = business.phone ?? ''
  businessContactForm.email = business.email ?? ''
  businessContactForm.website = business.website ?? ''
  businessContactForm.address = business.address ?? ''
  businessContactForm.latitude = business.latitude ?? ''
  businessContactForm.longitude = business.longitude ?? ''
  businessContactModalError.value = ''
  businessContactModalOpen.value = true
}

const closeBusinessContactModal = () => {
  businessContactModalOpen.value = false
  resetBusinessContactForm()
  activeBusinessId.value = null
}

const openServiceVendorModal = (module = null, business = null) => {
  resetServiceVendorForm()
  activeBusinessId.value = business?.id || module?.businessId || null
  if (module) {
    editingServiceModuleId.value = module.id
    serviceVendorForm.id = module.id
    serviceVendorForm.businessId = module.businessId
    serviceVendorForm.name = module.name
    serviceVendorForm.category = module.category
    serviceVendorForm.description = module.description
  } else {
    serviceVendorForm.businessId = business?.id || ''
  }
  serviceVendorModalOpen.value = true
}

const closeServiceVendorModal = () => {
  serviceVendorModalOpen.value = false
  resetServiceVendorForm()
  activeBusinessId.value = null
}

const saveServiceModule = async () => {
  const isEditing = Boolean(editingServiceModuleId.value)
  const payload = {
    id: editingServiceModuleId.value || Date.now(),
    businessId: serviceVendorForm.businessId || activeBusinessId.value || '',
    name: serviceVendorForm.name || 'Untitled service vendor',
    category: serviceVendorForm.category || 'Service vendor',
    description: serviceVendorForm.description || 'No description available.'
  }

  if (editingServiceModuleId.value) {
    serviceModules.value = serviceModules.value.map((item) => (item.id === editingServiceModuleId.value ? payload : item))
  } else {
    serviceModules.value.unshift(payload)
  }

  updateLastUpdated()
  closeServiceVendorModal()
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title: isEditing ? 'Service vendor updated' : 'Service vendor added',
    showConfirmButton: false,
    timer: 1600,
    timerProgressBar: true
  })
}

const deleteServiceModule = async (id) => {
  const result = await Swal.fire({
    title: 'Delete service vendor?',
    text: 'This service vendor module will be removed from the dashboard.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626'
  })

  if (!result.isConfirmed) return

  serviceModules.value = serviceModules.value.filter((item) => item.id !== id)
  updateLastUpdated()
}

const handleVendorMenuAction = (action, vendor) => {
  openBusinessMenuId.value = null
  if (action === 'edit') {
    openServiceModal(vendor)
    return
  }
  if (action === 'edit-contact') {
    openBusinessContactModal(vendor)
    return
  }
  if (action === 'delete') {
    deleteServiceVendor(vendor.id)
  }
}

const handleServiceImageChange = (event) => {
  clearServiceErrors()
  serviceModalError.value = ''
  const file = event.target.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    serviceErrors.image = 'Only image files are allowed'
    event.target.value = ''
    return
  }
  revokeImagePreview()
  serviceForm.imageFile = file
  serviceForm.imageName = file.name
  serviceImagePreview.value = URL.createObjectURL(file)
  event.target.value = ''
}

const removeServiceImage = () => {
  revokeImagePreview()
  serviceImagePreview.value = ''
  serviceForm.imageFile = null
  serviceForm.imageName = ''
  if (editingServiceId.value) {
    serviceForm.existingImage = ''
  }
}

const validateServiceForm = () => {
  clearServiceErrors()

  if (!serviceForm.businessName) serviceErrors.businessName = 'Business name is required'
  if (!serviceForm.ownerName) serviceErrors.ownerName = 'Owner name is required'
  if (!serviceForm.description) serviceErrors.description = 'Description is required'
  if (serviceForm.description && serviceForm.description.length < 10) serviceErrors.description = 'Description should be at least 10 characters'
  if (!editingServiceId.value && !serviceForm.imageFile) serviceErrors.image = 'Image is required'

  return !Object.values(serviceErrors).some(Boolean)
}

const saveServiceVendor = async () => {
  serviceModalError.value = ''
  if (!validateServiceForm()) return

  if (editingServiceId.value) {
    serviceVendors.value = serviceVendors.value.map((item) =>
      item.id === editingServiceId.value
        ? {
            ...item,
            businessName: serviceForm.businessName,
            ownerName: serviceForm.ownerName,
            description: serviceForm.description,
            image: serviceImagePreview.value || serviceForm.existingImage,
            badge: serviceForm.badge || item.badge
          }
        : item
    )
    updateLastUpdated()
    closeServiceModal()
    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Business updated locally',
      showConfirmButton: false,
      timer: 1600,
      timerProgressBar: true
    })
    return
  }

  vendorId.value = getAuthUserId()
  if (!vendorId.value) {
    serviceModalError.value = 'Vendor id (`s_id`) is not available. Sign in again before adding a business vendor.'
    return
  }

  serviceSubmitLoading.value = true
  try {
    const formData = new FormData()
    formData.append('s_id', vendorId.value)
    formData.append('business_name', serviceForm.businessName)
    formData.append('owner_name', serviceForm.ownerName)
    formData.append('description', serviceForm.description)
    formData.append('img', serviceForm.imageFile)

    const { data } = await api.addBusinessVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to add business')
    }

    closeServiceModal()
    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: data?.msg || 'Business added',
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true
    })
    await fetchBusinessVendors()
    updateLastUpdated()
  } catch (error) {
    serviceModalError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to add business'
  } finally {
    serviceSubmitLoading.value = false
  }
}

const saveBusinessContactInfo = async () => {
  businessContactModalError.value = ''
  if (!editingBusinessContactId.value) {
    businessContactModalError.value = 'Business id is missing.'
    return
  }

  businessContactSubmitLoading.value = true
  try {
    const formData = new FormData()
    formData.append('business_id', editingBusinessContactId.value)
    formData.append('phone_code', businessContactForm.phoneCode || '')
    formData.append('phone', businessContactForm.phone || '')
    formData.append('email', businessContactForm.email || '')
    formData.append('website', businessContactForm.website || '')
    formData.append('address', businessContactForm.address || '')
    formData.append('latitude', businessContactForm.latitude || '')
    formData.append('longitude', businessContactForm.longitude || '')

    const { data } = await api.updateBusinessContactVendor(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Failed to update business contact info')
    }

    serviceVendors.value = serviceVendors.value.map((item) =>
      item.id === editingBusinessContactId.value
        ? {
            ...item,
            phoneCode: businessContactForm.phoneCode,
            phone: businessContactForm.phone,
            email: businessContactForm.email,
            website: businessContactForm.website,
            address: businessContactForm.address,
            latitude: businessContactForm.latitude,
            longitude: businessContactForm.longitude
          }
        : item
    )

    updateLastUpdated()
    closeBusinessContactModal()
    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: data?.msg || 'Contact info updated',
      showConfirmButton: false,
      timer: 1600,
      timerProgressBar: true
    })
  } catch (error) {
    businessContactModalError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to update business contact info'
  } finally {
    businessContactSubmitLoading.value = false
  }
}

const deleteServiceVendor = async (id) => {
  const result = await Swal.fire({
    title: 'Delete business?',
    text: 'No delete API is wired yet, so this only removes the card locally for the current session.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626'
  })

  if (!result.isConfirmed) return

  serviceVendors.value = serviceVendors.value.filter((item) => item.id !== id)
  venues.value = venues.value.filter((item) => String(item.businessId) !== String(id))
  serviceModules.value = serviceModules.value.filter((item) => String(item.businessId) !== String(id))
  if (!serviceVendors.value.length) {
    closeVenueModal()
    closeServiceVendorModal()
  }
  updateLastUpdated()
}

onMounted(() => {
  console.log('Logged in vendor info:', {
    user: getAuthUser(),
    s_id: getAuthUserId()
  })
  fetchBusinessVendors()
})

onBeforeUnmount(() => {
  revokeImagePreview()
})
</script>
